if (!customElements.get('pc-outgrow-gallery')) {
  customElements.define(
    'pc-outgrow-gallery',
    class extends HTMLElement {
      async connectedCallback() {
        await customElements.whenDefined('media-gallery');
        if (!this.isConnected || this.gallery) return;

        this.gallery = this.querySelector('media-gallery');
        this.nativeSetActiveMedia = this.gallery.setActiveMedia.bind(this.gallery);
        this.side = 'front';
        this.frontMediaId = this.querySelector('.pc-outgrow-thumb[aria-current="true"]')?.dataset.frontMediaId;
        this.gallery.setActiveMedia = this.setActiveMedia.bind(this);
        this.onClick = this.handleClick.bind(this);
        this.addEventListener('click', this.onClick);

        this.track = this.querySelector('.pc-outgrow-track');
        this.models = this.querySelector('.pc-outgrow-models');
        this.pager = this.querySelector('.pc-outgrow-pager');
        this.slides = [this.querySelector('[id^="GalleryViewer-"]'), ...Array.from(this.models?.children || [])];
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.onLayoutChange = this.updateLayout.bind(this);
        this.onScroll = this.updateCounter.bind(this);
        this.mobile.addEventListener('change', this.onLayoutChange);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateLayout();
      }

      disconnectedCallback() {
        this.restoreScrollReset();
        this.removeEventListener('click', this.onClick);
        this.mobile?.removeEventListener('change', this.onLayoutChange);
        this.track?.removeEventListener('scroll', this.onScroll);
        if (this.gallery) this.gallery.setActiveMedia = this.nativeSetActiveMedia;
        this.gallery = null;
      }

      get thumbnails() {
        return Array.from(this.querySelectorAll('.pc-outgrow-thumb'));
      }

      setActiveMedia(mediaId) {
        const thumbnails = this.thumbnails;
        const requested = thumbnails.find((button) => button.dataset.mediaId === mediaId);
        if (!requested) return;
        if (requested.dataset.frontMediaId !== this.frontMediaId) this.side = 'front';
        this.frontMediaId = requested.dataset.frontMediaId;
        const selected = requested && (
          thumbnails.find((button) =>
            button.dataset.frontMediaId === requested.dataset.frontMediaId && button.dataset.side === this.side
          ) || requested
        );
        if (!selected) return;

        // Dawn supplies the primary image after each native variant update.
        // A new color starts at the front; changing only size keeps the view.
        this.side = selected.dataset.side;
        this.nativeSetActiveMedia(selected.dataset.mediaId, false);
        thumbnails.forEach((button) => {
          button.hidden = button.dataset.frontMediaId !== this.frontMediaId;
          if (button === selected) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        this.querySelector('[id^="GalleryStatus-"]').textContent = selected.getAttribute('aria-label');
        this.scrollToSlide(0, true);
      }

      updateLayout() {
        this.restoreScrollReset();
        // Move the existing model list, leaving Dawn's first media UL intact.
        // Restore the original desktop DOM and keyboard focus order on resize.
        if (this.models) {
          if (this.mobile.matches) this.track.append(this.models);
          else this.gallery.append(this.models);
        }
        this.pager.hidden = !this.mobile.matches || this.slides.length < 2;
        this.track.scrollTo({ left: 0, behavior: 'auto' });
        this.updateCounter();
      }

      updateCounter() {
        if (!this.mobile.matches) return;
        const position = this.track.scrollLeft;
        this.currentSlide = this.slides.reduce((nearest, slide, index) =>
          Math.abs(slide.offsetLeft - position) < Math.abs(this.slides[nearest].offsetLeft - position) ? index : nearest,
        0);
        this.pager.querySelector('[data-gallery-current]').textContent = this.currentSlide + 1;
        this.pager.querySelector('[data-gallery-total]').textContent = this.slides.length;
        this.pager.querySelector('[data-gallery-step="-1"]').disabled = this.currentSlide === 0;
        this.pager.querySelector('[data-gallery-step="1"]').disabled = this.currentSlide === this.slides.length - 1;
      }

      scrollToSlide(index, immediate = false) {
        this.restoreScrollReset();
        if (!this.mobile?.matches) return;
        const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
        const left = slide.offsetLeft;
        if (immediate) {
          // A touch fling can resume after scrollTo(0). Remove its scrolling
          // area for one painted frame before restoring normal swipe behavior.
          const properties = ['overflow-x', 'overflow-y', 'scroll-snap-type'];
          this.scrollResetStyles = properties.map((name) => [
            name, this.track.style.getPropertyValue(name), this.track.style.getPropertyPriority(name),
          ]);
          this.track.style.setProperty('overflow-x', 'clip');
          this.track.style.setProperty('overflow-y', 'clip');
          this.track.style.setProperty('scroll-snap-type', 'none');
          this.track.scrollTo({ left, behavior: 'auto' });
          void this.track.offsetWidth;
          this.updateCounter();
          this.scrollResetFrame = requestAnimationFrame(() => {
            this.scrollResetFrame = requestAnimationFrame(() => {
              this.restoreScrollReset();
              this.track.scrollTo({ left, behavior: 'auto' });
              this.updateCounter();
            });
          });
          return;
        }
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
      }

      restoreScrollReset() {
        if (this.scrollResetFrame) cancelAnimationFrame(this.scrollResetFrame);
        this.scrollResetFrame = undefined;
        this.scrollResetStyles?.forEach(([name, value, priority]) => {
          if (value) this.track.style.setProperty(name, value, priority);
          else this.track.style.removeProperty(name);
        });
        this.scrollResetStyles = undefined;
      }

      handleClick(event) {
        const step = event.target.closest('[data-gallery-step]');
        if (step && this.contains(step)) {
          this.scrollToSlide(this.currentSlide + Number(step.dataset.galleryStep));
          return;
        }
        const button = event.target.closest('.pc-outgrow-thumb');
        if (!button || !this.contains(button) || button.hidden) return;

        this.side = button.dataset.side;
        this.setActiveMedia(button.dataset.mediaId);
      }
    }
  );
}
